'use client';

import * as braze from '@braze/web-sdk';
import { datadogRum } from '@datadog/browser-rum';
import { isOtPerfEnabled, OT_CONSENT_COOKIE_NAME } from '@ab-inbev-eu/utils/onetrust';

import { getCurrentUserId } from '../../actions/session.actions';
import { getCookie } from '../functions.client';
import { publicConfig } from '../config';

import { getBrazeAnonymousId } from './anonymous.client';

type InitFailureReason = 'missing_config' | 'initialize_returned_false';
type BrazeInitSource = 'load' | 'consent' | 'mount' | 'event';

let initInFlight: Promise<boolean> | null = null;

function getBrazeUserId(): string | null {
  return braze.getUser()?.getUserId() ?? null;
}

function hasPerformanceConsent(): boolean {
  return isOtPerfEnabled(decodeURIComponent(getCookie(OT_CONSENT_COOKIE_NAME) ?? ''));
}

function getBrazeDebugContext() {
  return {
    braze_initialized: braze.isInitialized(),
    braze_user_id: getBrazeUserId(),
    braze_anonymous_id: getBrazeAnonymousId(),
    performance_consent: hasPerformanceConsent(),
    init_in_flight: initInFlight !== null,
  };
}

function logInitFailure(reason: InitFailureReason, errorMessage: string) {
  datadogRum.addError(new Error('BRAZE_INIT_FAILED'), {
    error_message: errorMessage,
    reason,
    ...getBrazeDebugContext(),
  });
}

async function waitForBrazeInit(): Promise<void> {
  if (initInFlight) {
    await initInFlight;
  }
}

function startBrazeInit(source: BrazeInitSource): void {
  if (initInFlight) {
    datadogRum.addAction('Braze init: skipped', {
      reason: 'init_in_flight',
      init_source: source,
      ...getBrazeDebugContext(),
    });
    return;
  }

  datadogRum.addAction('Braze init: scheduled', {
    init_source: source,
    ...getBrazeDebugContext(),
  });

  initInFlight = initializeBraze(source).finally(() => {
    initInFlight = null;
  });
}

export function ensureBrazeInit(source: BrazeInitSource = 'event'): void {
  if (braze.isInitialized()) return;

  startBrazeInit(source);
}

export async function ensureBrazeSession(): Promise<void> {
  if (!braze.isInitialized()) return;

  const uid = await getCurrentUserId();
  if (uid) {
    braze.changeUser(uid);
  }
  braze.openSession();
}

function configureInitializedSdk(): void {
  braze.automaticallyShowInAppMessages();
  braze.openSession();

  const language = document.documentElement.lang || undefined;
  if (language) {
    braze.getUser()?.setLanguage(language);
  }
}

async function initializeBraze(source: BrazeInitSource): Promise<boolean> {
  if (braze.isInitialized()) {
    return true;
  }

  const {
    BRAZE: { API_KEY, SDK_ENDPOINT },
  } = publicConfig();

  const noCookies = !hasPerformanceConsent();

  if (!API_KEY || !SDK_ENDPOINT) {
    logInitFailure('missing_config', 'Braze init skipped: missing API key or SDK endpoint');
    return false;
  }

  const isInitialized = braze.initialize(API_KEY, {
    baseUrl: SDK_ENDPOINT,
    noCookies,
    allowUserSuppliedJavascript: true,
  });

  if (!isInitialized) {
    logInitFailure('initialize_returned_false', 'Braze initialize() returned false');
    return false;
  }

  // In-app triggers are fetched for the current user when the session opens.
  try {
    const uid = await getCurrentUserId();
    if (uid) {
      braze.changeUser(uid);
    }
  } catch (error) {
    datadogRum.addError(error instanceof Error ? error : new Error('BRAZE_CHANGE_USER_FAILED'), {
      error_message: error instanceof Error ? error.message : 'Braze changeUser before session failed',
      ...getBrazeDebugContext(),
    });
  }

  configureInitializedSdk();

  datadogRum.addAction('Braze init: success', {
    init_source: source,
    no_cookies: noCookies,
    braze_user_id: getBrazeUserId(),
  });

  return true;
}

export function mountBrazeInit(): () => void {
  datadogRum.addAction('Braze mount: attached', {
    document_ready_state: document.readyState,
    ...getBrazeDebugContext(),
  });

  const onLoad = () => {
    ensureBrazeInit('load');
  };

  const onConsent = () => {
    ensureBrazeInit('consent');
  };

  if (document.readyState === 'complete') {
    ensureBrazeInit('mount');
  } else {
    window.addEventListener('load', onLoad, { once: true });
  }

  window.addEventListener('consent.onetrust', onConsent);

  return () => {
    datadogRum.addAction('Braze mount: detached', getBrazeDebugContext());

    window.removeEventListener('load', onLoad);
    window.removeEventListener('consent.onetrust', onConsent);
  };
}

export async function ensureBrazeReady(): Promise<boolean> {
  ensureBrazeInit('event');
  await waitForBrazeInit();
  const ready = braze.isInitialized();
  if (!ready) {
    datadogRum.addAction('Braze ready: false', getBrazeDebugContext());
  }

  return ready;
}
